import { useNotifications } from '@/context/notifications/notifications'
import { useMutation, useQuery } from '@apollo/client'
import { captureException } from '@sentry/browser'
import React, { useCallback, useState } from 'react'

import { SectionWrapper } from '@/components/elements/atoms/SectionWrapper'
import SkeletonButton from '@/components/elements/atoms/SkeletonButton/SkeletonButton'
import SkeletonParagraph from '@/components/elements/atoms/SkeletonParagraph/SkeletonParagraph'
import { Switch } from '@/components/elements/atoms/Switch/Switch'
import Text from '@/components/elements/atoms/Text'

import STYLES from '../ContactPreferencesPage.module.sass'

import { SMS_DELIVERY_REMINDER_MUTATION } from '../mutations/smsDeliveryReminderMutation'
import { SMS_DELIVERY_REMINDER_QUERY } from '../queries/smsDeliveryReminderQuery'

const SMSDeliveryReminder = (): JSX.Element => {
  const [checked, setChecked] = useState(false)
  const { setSuccessNotification, setErrorNotification } = useNotifications()
  const { loading, error } = useQuery(SMS_DELIVERY_REMINDER_QUERY, {
    onCompleted: (data) => {
      setChecked(!!data?.user?.shouldSendSmsDeliveryReminder)
    },
    onError: (error) => {
      setErrorNotification({
        text: 'contact_preferences.sms_delivery_reminder.error.fetching',
        namespace: 'account'
      })
      captureException(error)
    }
  })
  const [updateSmsDeliveryReminder, { loading: updating }] = useMutation(
    SMS_DELIVERY_REMINDER_MUTATION,
    {
      onCompleted: (data) => {
        const result =
          !!data?.smsDeliveryReminderUpdate?.shouldSendSmsDeliveryReminder
        const successText = result
          ? 'contact_preferences.sms_delivery_reminder.success.enabled'
          : 'contact_preferences.sms_delivery_reminder.success.disabled'
        setSuccessNotification({
          text: successText,
          namespace: 'account'
        })
        setChecked(result)
      },
      onError: (error) => {
        setErrorNotification({
          text: 'contact_preferences.sms_delivery_reminder.error.updating',
          namespace: 'account'
        })
        captureException(error)
        setChecked((prevChecked) => !prevChecked)
      }
    }
  )

  const onChange = useCallback(
    (e) => {
      setChecked(e.target.checked)
      updateSmsDeliveryReminder({
        variables: { smsDeliveryReminder: e.target.checked }
      })
    },
    [updateSmsDeliveryReminder]
  )

  return (
    <SectionWrapper
      headerTypography={{
        text: 'contact_preferences.sms_delivery_reminder.title',
        namespace: 'account',
        variant: 'display16',
        margin: false
      }}
      borderRadius={16}
      bgColour="brandWhite"
      padding={32}
      margin={0}
    >
      {loading ? (
        <div className={STYLES.SMSWrapper}>
          <div className={STYLES.SMSSwitch}>
            <SkeletonButton width={36} height={24} />
          </div>
          <SkeletonParagraph
            width={400}
            count={1}
            height={18}
            shortLine={false}
          />
        </div>
      ) : error ? (
        <Text
          text="contact_preferences.sms_delivery_reminder.error.display"
          namespace="account"
        />
      ) : (
        <div className={STYLES.SMSWrapper}>
          <div className={STYLES.SMSSwitch}>
            <Switch checked={checked} disabled={updating} onChange={onChange} />
          </div>
          <Text
            text="contact_preferences.sms_delivery_reminder.label"
            namespace="account"
          />
        </div>
      )}
    </SectionWrapper>
  )
}

export { SMSDeliveryReminder }
